import { ensureFreshSession } from "@auth/refreshMiddleware";
import { getActiveSession } from "@auth/sessionStore";
import { createMicSource, type PcmSource } from "@transcription/pcmSource";
import {
  beginRecording,
  enqueueChunk,
  finishRecording,
  setRecordingTranscript,
} from "@transcription/recordingsClient";
import { useCallback, useEffect, useRef, useState } from "react";

import { getConfig } from "../config";

export type TranscriptionStatus =
  | "idle"
  | "connecting"
  | "recording"
  | "reconnecting"
  | "stopping"
  | "error";

export interface Turn {
  order: number;
  transcript: string;
  endOfTurn: boolean;
  formatted: boolean;
}

interface UseLiveTranscriptionOptions {
  keyterms?: string[];
}

export interface StartOptions {
  // Audio producer. Defaults to the live mic; dev injection passes a fixture
  // source (see @transcription/dev/fixtureSource).
  source?: PcmSource;
  // Override where the stored audio artifact is anchored. Defaults to the
  // clinic — a storage anchor only, never clinical attribution (the agent
  // attributes patients/visits from the conversation). The lab's room mode
  // passes the room, mirroring how Zuul's uploads anchor to their room.
  about?: string[];
  // Override the WS auth token (defaults to the user session). A provider,
  // not a value, so each (re)connect gets a fresh token — dev injection uses
  // this to stream under a room-scope token like an ambient device would.
  wsToken?: () => Promise<string>;
}

export interface UseLiveTranscriptionResult {
  status: TranscriptionStatus;
  turns: Turn[];
  partial: string;
  error: string | null;
  start: (options?: StartOptions) => Promise<void>;
  stop: () => void;
  noAudioWarning: boolean;
  /**
   * The transcriber has returned at least one turn this session, so audio is confirmed to
   * be getting all the way through. A latch: true on the first turn and true until the next
   * `start()`, so it reports "we have heard you", not "we can hear you right now" — it
   * won't fall back to false if the mic dies mid-session.
   *
   * The state form of the signal that arms `noAudioWarning`, the 15-second "still nothing"
   * alarm covering the window while this is false.
   */
  audioDetected: boolean;
}

const DEFAULT_WS_URL = getConfig().wsUrl;
const MAX_TURNS = 500;
const STOP_TIMEOUT_MS = 3000;
const FLUSH_BYTES = 512 * 1024;
const MAX_RECONNECTS = 12;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15_000;
const NO_AUDIO_TIMEOUT_MS = 15_000;

function concatBuffers(chunks: ArrayBuffer[]): ArrayBuffer {
  const total = chunks.reduce((n, c) => n + c.byteLength, 0);
  const merged = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(new Uint8Array(chunk), offset);
    offset += chunk.byteLength;
  }
  return merged.buffer;
}

export function useLiveTranscription({
  keyterms,
}: UseLiveTranscriptionOptions = {}): UseLiveTranscriptionResult {
  const wsUrl = `${DEFAULT_WS_URL}/ws/transcription/stream`;
  const [status, setStatus] = useState<TranscriptionStatus>("idle");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [partial, setPartial] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [noAudioWarning, setNoAudioWarning] = useState(false);
  const [audioDetected, setAudioDetected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const sourceRef = useRef<PcmSource | null>(null);
  const readyResolveRef = useRef<(() => void) | null>(null);
  const turnOrderRef = useRef(0);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noAudioTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioDetectedRef = useRef(false);
  const noAudioArmedRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const openSocketRef = useRef<(() => Promise<void>) | null>(null);
  const scheduleReconnectRef = useRef<(() => void) | null>(null);
  const userStoppingRef = useRef(false);
  const recordingIdRef = useRef<string | null>(null);
  const batchRef = useRef<ArrayBuffer[]>([]);
  const batchBytesRef = useRef(0);
  const seqRef = useRef(0);
  const stopRef = useRef<(() => void) | null>(null);
  const wsTokenRef = useRef<(() => Promise<string>) | null>(null);

  const finalizeRecording = useCallback(() => {
    const recordingId = recordingIdRef.current;
    recordingIdRef.current = null;
    if (!recordingId) return;
    const tail = batchRef.current;
    batchRef.current = [];
    batchBytesRef.current = 0;
    finishRecording(recordingId, tail, seqRef.current).catch(() => {});
  }, []);

  const teardownSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (
      ws.readyState !== WebSocket.CLOSED &&
      ws.readyState !== WebSocket.CLOSING
    )
      ws.close();
  }, []);

  const cleanup = useCallback(() => {
    finalizeRecording();
    if (stopTimerRef.current !== null) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (noAudioTimerRef.current !== null) {
      clearTimeout(noAudioTimerRef.current);
      noAudioTimerRef.current = null;
    }
    setNoAudioWarning(false);
    setAudioDetected(false);
    sourceRef.current?.stop();
    sourceRef.current = null;
    // Release any source still awaiting socket-open so it unwinds.
    readyResolveRef.current?.();
    readyResolveRef.current = null;
    wsTokenRef.current = null;
    teardownSocket();
  }, [finalizeRecording, teardownSocket]);

  // Forward one PCM chunk to both sinks the pipeline depends on: the live ASR
  // socket (best-effort while open) and the resumable IndexedDB recording that
  // becomes the uploaded artifact. The mic worklet and dev injection share it.
  const handleChunk = useCallback((chunk: ArrayBuffer) => {
    if (!noAudioArmedRef.current && !userStoppingRef.current) {
      noAudioArmedRef.current = true;
      noAudioTimerRef.current = setTimeout(() => {
        noAudioTimerRef.current = null;
        if (!audioDetectedRef.current) setNoAudioWarning(true);
      }, NO_AUDIO_TIMEOUT_MS);
    }
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(chunk);
    const recordingId = recordingIdRef.current;
    if (!recordingId) return;
    batchRef.current.push(chunk);
    batchBytesRef.current += chunk.byteLength;
    if (batchBytesRef.current >= FLUSH_BYTES) {
      const merged = concatBuffers(batchRef.current);
      batchRef.current = [];
      batchBytesRef.current = 0;
      enqueueChunk(recordingId, seqRef.current++, merged);
    }
  }, []);

  // The mic, worklet and local persistence outlive any single socket: a dropped
  // connection rebuilds only the WebSocket, so audio capture (and the resumable
  // recording in IndexedDB) keeps running while we reconnect.
  const scheduleReconnect = useCallback(() => {
    if (userStoppingRef.current) return;
    teardownSocket();
    if (reconnectAttemptsRef.current >= MAX_RECONNECTS) {
      setError("Connection lost");
      setStatus("error");
      cleanup();
      return;
    }
    reconnectAttemptsRef.current += 1;
    setStatus("reconnecting");
    const wait = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * 2 ** (reconnectAttemptsRef.current - 1),
    );
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      openSocketRef.current?.().catch(() => scheduleReconnectRef.current?.());
    }, wait);
  }, [cleanup, teardownSocket]);

  useEffect(() => {
    scheduleReconnectRef.current = scheduleReconnect;
  }, [scheduleReconnect]);

  const handleMessage = useCallback(
    (raw: string) => {
      let msg: {
        type?: string;
        transcript?: string;
        transcript_uuid?: string;
        end_of_turn?: boolean;
        turn_is_formatted?: boolean;
      };
      try {
        msg = JSON.parse(raw);
      } catch {
        return;
      }
      if (msg.type === "Turn" && typeof msg.transcript === "string") {
        // First turn of the session: audio is confirmed to reach the transcriber,
        // so disarm the no-audio alarm and latch the signal on for the UI. The ref
        // is what the alarm reads (it's set from a callback that can't depend on
        // state); the state is the same fact, exposed for rendering.
        if (!audioDetectedRef.current) {
          audioDetectedRef.current = true;
          setAudioDetected(true);
          if (noAudioTimerRef.current !== null) {
            clearTimeout(noAudioTimerRef.current);
            noAudioTimerRef.current = null;
          }
          setNoAudioWarning(false);
        }
        const text = msg.transcript;
        const endOfTurn = !!msg.end_of_turn;
        const formatted = !!msg.turn_is_formatted;
        if (endOfTurn) {
          const order = turnOrderRef.current++;
          setTurns((prev) => {
            const next = [
              ...prev,
              { order, transcript: text, endOfTurn: true, formatted },
            ];
            return next.length > MAX_TURNS
              ? next.slice(next.length - MAX_TURNS)
              : next;
          });
          setPartial("");
        } else {
          setPartial(text);
        }
      } else if (msg.type === "Termination") {
        if (userStoppingRef.current) {
          setStatus("idle");
          cleanup();
        } else {
          scheduleReconnect();
        }
      } else if (msg.type === "Begin") {
        reconnectAttemptsRef.current = 0;
        setStatus("recording");
      } else if (msg.type === "started") {
        const recordingId = recordingIdRef.current;
        if (recordingId && msg.transcript_uuid)
          setRecordingTranscript(recordingId, msg.transcript_uuid).catch(
            () => {},
          );
      }
    },
    [cleanup, scheduleReconnect],
  );

  const openSocket = useCallback(async () => {
    const token = wsTokenRef.current
      ? await wsTokenRef.current()
      : (await ensureFreshSession())?.accessToken;
    if (userStoppingRef.current) return;
    if (!token) throw new Error("Not authenticated");
    const ws = new WebSocket(wsUrl, ["scribenote.auth.bearer.v1", token]);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "start", keyterms: keyterms ?? [] }));
      setStatus((s) => (s === "stopping" ? s : "recording"));
      readyResolveRef.current?.();
      readyResolveRef.current = null;
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") handleMessage(ev.data);
    };
    ws.onerror = scheduleReconnect;
    ws.onclose = () => {
      if (!userStoppingRef.current) scheduleReconnect();
    };
  }, [wsUrl, keyterms, handleMessage, scheduleReconnect]);

  useEffect(() => {
    openSocketRef.current = openSocket;
  }, [openSocket]);

  const start = useCallback(
    async (options?: StartOptions) => {
      if (wsRef.current || sourceRef.current) return;
      if (status !== "idle" && status !== "error") return;
      setError(null);
      setTurns([]);
      setPartial("");
      turnOrderRef.current = 0;
      userStoppingRef.current = false;
      wsTokenRef.current = options?.wsToken ?? null;
      reconnectAttemptsRef.current = 0;
      audioDetectedRef.current = false;
      noAudioArmedRef.current = false;
      setNoAudioWarning(false);
      setAudioDetected(false);
      const recordingId = crypto.randomUUID();
      recordingIdRef.current = recordingId;
      const clinicUuid = getActiveSession()?.clinicUuid;
      const about = options?.about ?? (clinicUuid ? [clinicUuid] : []);
      beginRecording(recordingId, about).catch(() => {});
      batchRef.current = [];
      batchBytesRef.current = 0;
      seqRef.current = 0;
      setStatus("connecting");

      const source = options?.source ?? createMicSource();
      sourceRef.current = source;
      const whenReady = new Promise<void>((resolve) => {
        readyResolveRef.current = resolve;
      });

      try {
        await source.start({
          emit: handleChunk,
          whenReady,
          onEnded: () => stopRef.current?.(),
        });
        await openSocket();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
        cleanup();
      }
    },
    [status, openSocket, handleChunk, cleanup],
  );

  const stop = useCallback(() => {
    if (
      status !== "recording" &&
      status !== "connecting" &&
      status !== "reconnecting"
    )
      return;
    userStoppingRef.current = true;
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setStatus("stopping");
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "stop" }));
      } catch {
        cleanup();
        setStatus("idle");
        return;
      }
      stopTimerRef.current = setTimeout(() => {
        stopTimerRef.current = null;
        cleanup();
        setStatus("idle");
      }, STOP_TIMEOUT_MS);
    } else {
      cleanup();
      setStatus("idle");
    }
  }, [status, cleanup]);

  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  useEffect(() => cleanup, [cleanup]);

  return {
    status,
    turns,
    partial,
    error,
    start,
    stop,
    noAudioWarning,
    audioDetected,
  };
}
